import listService from 'ListService';
import constants from 'Constants';
import mobileConstants from './../../mobile/commons/MobileFormsConstants';
import mobileFactoryService from "./MobileFactoryService";
import Immutable from 'immutable';
import AllowedControl from './../../mobile/commons/AllowedControl';
import util from '../util/Util';
import utilMobile from "../../mobile/commons/MobileGeneralFunctions";

class MobileListService {
    constructor() {
      this._mobileCurrentColumnView = constants.ONE_COLUMN_VIEW_DROPDOWN_TEXT;
      this._mobileAutoOpenWizard = false;
      this._mobilePageSelected = null;
      this._wantsToAddMoreObjects = false;
      this._mobileTreeState = Immutable.Map();
      this._mobileDesigner = true;
      this._mobileObjMap = Immutable.Map();
    }

    //============================================================
    // mobileCurrentColumnView set and get the current column view for mobile designer
    //============================================================
    get mobileCurrentColumnView() { return this._mobileCurrentColumnView; }
    set mobileCurrentColumnView(arg) { this._mobileCurrentColumnView = arg; }

    get mobileAutoOpenWizard() { return this._mobileAutoOpenWizard; }
    set mobileAutoOpenWizard(arg) { this._mobileAutoOpenWizard = arg; }

    set mobilePageSelected(value) { this._mobilePageSelected = value;}
    get mobilePageSelected() { return this._mobilePageSelected;}

    set wantsToAddMoreObjects(value) { this._wantsToAddMoreObjects = value;}
    get wantsToAddMoreObjects() { return this._wantsToAddMoreObjects;}

    get mobileDesigner() { return this._mobileDesigner; }

    getMobileObjects() {
        return listService._mobileObjects;
    }

    mobileObjMapHasObjects(){
       return this._mobileObjMap.size > 0;
    }



    isSection(id) {
        return listService.getSectionById(id) !== undefined;
    }

    addTreeState( key, branch ){
        this._mobileTreeState = this._mobileTreeState.set( key, branch );
        return this._mobileTreeState;
    }

    getTreeState( key ) {
        return this._mobileTreeState.get( key );
    }

    clearMobileTreeState(options = {}) {
        this._mobileTreeState = this._mobileTreeState.clear( options );
        return this._mobileTreeState;
    }

    get mobileTreeState() { return this._mobileTreeState; }


    addMobileControl(control) {
        this._mobileObjMap = this._mobileObjMap.set(control.id, control);
    }

    deleteMobileControlbyId (id) {
        this._mobileObjMap =  this._mobileObjMap.delete(id);
        return this._mobileObjMap;
    }

    getMobileControl(id) {
        return this._mobileObjMap.get(id);
    }

    getMobileControls() {
        return this._mobileObjMap;
    }

    updateMobileControl(id, control) {
        this._mobileObjMap =  this._mobileObjMap.set(id, control);
    }

    pageHasSections(pageId) {
        const hasSections = listService.getSectionsByPageId(pageId);
        return hasSections && hasSections.size > 0;
    }

    resetMobileObjMap() {
        this._mobileObjMap = this._mobileObjMap.clear();
        this._mobileObjMap = Immutable.OrderedMap();
    }

    updateMobileObjMap(mobileForm) {
        this.resetMobileObjMap(); 

        const form = listService.getObject(constants.FORM_RAW_ID);
        if (form && form.model) {
            const marginSettings = listService.getMobileStyleByKey(mobileConstants.MARGIN_KEY);
            const marginSpacing = listService.getMobileSetting("marginSpacing");
            const screenColor = mobileForm?.Form?.Properties?._outsideColor || "eeeeee";
            form.model.set('defaultMargin', marginSettings.defaultMargin, constants.MODEL_SILENT);
            form.model.set('defaultMarginSize', marginSpacing, constants.MODEL_SILENT);
            form.model.set('mobileOutsideColor', '#' + screenColor, constants.MODEL_SILENT);
            this.addMobileControl(form);
        }

        listService.mobileSections().forEach(section => this._updateSectionView(section));

        listService.mobileObjects.forEach(object => this._updateControlView(object));
    }

    updateAllMobileControlsModels() {     
        let mobileSections = listService.mobileSections();
        if (mobileSections){
            mobileSections.forEach(section => this._updateSectionView(section));
        }        
        
        listService.mobileObjects.forEach(object => this._updateControlView(object));
    }

    _updateSectionView(sectionData) {
        let sectionView = this.getMobileControl(sectionData.id);

        // If the view doesn't exist in our map, create it.
        if (!sectionView) {
            const key = mobileConstants.SECTION_FACTORY_KEY;
            sectionView = mobileFactoryService.factories[key].factory.createView(sectionData, 'column1-content');
            sectionView.id = sectionData.id;
            this.addMobileControl(sectionView);
        }

        // Now that we have a view, sync its model with the latest data.
        this._syncSectionModel(sectionView.model, sectionData);
    }

    _syncSectionModel(model, sectionData) {
        const props = sectionData.attributes || {};
        const marginSettings = listService.getMobileStyleByKey(mobileConstants.MARGIN_KEY);

        model.set({
            'id': sectionData.id,
            'name': sectionData.name,
            'hasTitle': props.hasTitle !== undefined ? props.hasTitle : true,
            'titleText': props.titleText || sectionData.name,
            'titleAlign': props.titleAlign || 'left',
            'titleHeight': props.titleHeight || 'auto',
            'titleColor': util._fixColorFormat(props.titleColor || '#000000'),
            'titleFontFamily': props.titleFontFamily || 'sans-serif',
            'titleFontSize': props.titleFontSize,
            'titleTextWeight': props.titleTextWeight || 'normal',
            'titleTextStyle': props.titleTextStyle || 'normal',
            'titleBackgroundColor': util._fixColorFormat(props.titleBackgroundColor || '#FFFFFF'),
            'borderColor': util._fixColorFormat(props.borderColor || '#C0C0C0'),
            'titleBoldSelection': props.titleBoldSelection || '',
            'titleItalicSelection': props.titleItalicSelection || '',
            'cornerRadius': props.cornerRadius || '0',
            'borderSize': props.borderSize || '1',
            'borderStyle': props.borderStyle || 'solid',
            'backgroundColor': util._fixColorFormat(props.backgroundColor || '#ffffff'),
            'margins': sectionData.margins || mobileConstants.DEFAULT_MARGINS,
            'marginSize': marginSettings.marginSize
        }, { silent: true });
    }

    _updateControlView(objectData) {
        const formControl = listService.getObject(objectData.id);
        if (!formControl) return; // Skip if desktop control was deleted

        let isASupportedGroup = false;
        if (objectData.factoryKey === 'control_group') {
            const childList = formControl.model.get('childList');
            if (childList) {
                const supportedChildren = (cl, fk) => cl.every(c => c.get('factoryKey') === fk);
                isASupportedGroup = supportedChildren(childList, 'control_checkbox') || supportedChildren(childList, 'control_radio_button');
            }
        }
        if (!AllowedControl.isAllowed(objectData.factoryKey) && !isASupportedGroup) {
            return;
        }

        let mobileControlView = this.getMobileControl(objectData.id);

        if (!mobileControlView) {
            mobileControlView = mobileFactoryService.factories[objectData.factoryKey].factory.createView(objectData.factoryKey, undefined);
            mobileControlView.id = objectData.id;
            this.addMobileControl(mobileControlView);
        }

        this._syncControlModel(mobileControlView, objectData, formControl);
    }

    _syncControlModel(mobileControlView, objectData, formControl) {
        const marginSettings = listService.getMobileStyleByKey(mobileConstants.MARGIN_KEY);

        if (objectData.attributes && objectData.attributes.margins) {
            delete objectData.attributes.margins;
        }
        this.applyMobileSettings(mobileControlView, objectData.attributes, formControl);

        objectData.margins = utilMobile.adjustControlMargins(objectData);

        if (objectData.factoryKey === 'control_table') {
            mobileControlView.model.set("headerFontSize", objectData.attributes.fontSize, constants.MODEL_SILENT);
        }

        mobileControlView.model.set({
            'id': objectData.id,
            "fontSize": objectData.attributes.fontSize,
            "margins": objectData.margins,
            "height": objectData.height,
            'marginSize': marginSettings.marginSize,
            "state": objectData.state,
            "mandatories": objectData.mandatories,
            "container": `sectionBodyContainer_${objectData.sectionId}`
        }, { silent: true });

        if (objectData.attributes.styleColorText) {
            mobileControlView.model.set("styleColorText", objectData.attributes.styleColorText, constants.MODEL_SILENT);
        }

        mobileControlView.groupId = formControl.groupId;
        if (mobileControlView.groupId && [mobileConstants.CHECKBOX_FACTORY_KEY, mobileConstants.RADIO_BUTTON_FACTORY_KEY].includes(objectData.factoryKey)) {
            mobileControlView.model.set('tabIndex', objectData.attributes.tabIndex, constants.MODEL_SILENT);
            mobileControlView.model.set('groupContainer', `groupItems${formControl.groupId}`, constants.MODEL_SILENT);
        }
    }

    applyMobileSettings(mobileControl, mobileSettings, formControl){
        const formModel = formControl.model.clone();
        const mergedProps = formModel.attributes;
        const factoryKey = mobileControl.model.get("factoryKey");

        this.setMobileTextStyles(mobileSettings, mergedProps);
        this.setMobileBorderStyles(mobileSettings, mergedProps);
        this.setMobileAlignmentSetting(mobileSettings, mergedProps);
        this.setMobileBackgroundStyle(mobileSettings, mergedProps, factoryKey);
        this.setMobileButtonsSettings(mobileSettings, mergedProps);
        this.setMobileHorizontalSliderStyles(mobileSettings, mergedProps);
        this.setMobileHorizontalSeparatorStyles(mobileSettings, mergedProps);
        this.setMobileWebSignatureStyles(mobileSettings, mergedProps);
        this.setMobileProgressBarSettings(mobileSettings, mergedProps);
        this.setMobileCaptchaSettings(mobileSettings, mergedProps);
        this.setMobilePictureSettings(mobileSettings, mergedProps);
        this.setMobileFileAttachment(mobileSettings, mergedProps);

        mobileControl.model.set(mergedProps, constants.MODEL_SILENT);
    }
    
    setMobileBackgroundStyle(mobileSettings, mergedProps, factoryKey){
        if(mobileSettings.backgroundColor){
            mergedProps.backgroundColor = util._fixColorFormat(mobileSettings.backgroundColor);

            if(factoryKey === "control_file_attachment"){
                mergedProps.backgroundColor = "FFFFFF";
            }
        }
        
        if(mobileSettings.bgGradientColor){
            mergedProps.bgGradientColor = util._fixColorFormat(mobileSettings.bgGradientColor);
            mergedProps.advancedBgGradientColor = util._fixColorFormat(mobileSettings.bgGradientColor);
        } 

        if(mobileSettings.backgroundDownColor){
            mergedProps.advancedBgColorDown = util._fixColorFormat(mobileSettings.backgroundDownColor);
        }

        if(mobileSettings.bgGradientDownColor){
            mergedProps.advancedBgGradientColorDown = util._fixColorFormat(mobileSettings.bgGradientDownColor);
        }
    }

    setMobileButtonsSettings(mobileSettings, mergedProps){
        if(mobileSettings.buttonStyle){
            mergedProps.buttonStyle = mobileSettings.buttonStyle;
        }
    }

    setMobileWebSignatureStyles(mobileSettings, mergedProps){
        if(mobileSettings.buttonTextColor){
            mergedProps.buttonTextColor = util._fixColorFormat(mobileSettings.buttonTextColor);
        }

        if(mobileSettings.buttonBackgroundColor){
            mergedProps.buttonBackgroundColor = util._fixColorFormat(mobileSettings.buttonBackgroundColor);
        }

        if(mobileSettings.buttonBackgroundGradientColor){
            mergedProps.buttonBackgroundGradientColor = util._fixColorFormat(mobileSettings.buttonBackgroundGradientColor);
        }

        if(mobileSettings.imageBackgroundColor){
            mergedProps.imageBackgroundColor = util._fixColorFormat(mobileSettings.imageBackgroundColor);
        }

        if(mobileSettings.signatureColor){
            mergedProps.signatureColor = util._fixColorFormat(mobileSettings.signatureColor);
        }

        if(mobileSettings.signatureThickness){
            mergedProps.signatureThickness = mobileSettings.signatureThickness;
        }
    }

    setMobileProgressBarSettings(mobileSettings, mergedProps){
        if(mobileSettings.showLabel){
            mergedProps.showLabel = mobileSettings.showLabel;
        }

        if(mobileSettings.label){
            mergedProps.label = mobileSettings.label;
        }

        if(mobileSettings.barColor){
            mergedProps.barColor = util._fixColorFormat(mobileSettings.sliderColor);
        }
    }

    setMobileCaptchaSettings(mobileSettings, mergedProps){
        if(mobileSettings.length){
            mergedProps.length = mobileSettings.length;
        }
    }

    setMobilePictureSettings(mobileSettings, mergedProps){
        if(mobileSettings.maintainAspectRatio){
            mergedProps.maintainAspectRatio = mobileSettings.maintainAspectRatio;
        }

        if(mobileSettings.stretch){
            mergedProps.stretch = mobileSettings.stretch;
        }
    }

    setMobileFileAttachment(mobileSettings, mergedProps){
        const keysToSync = [
            'buttonFontFamily', 
            'buttonFontSize', 
            'buttonTextStyle', 
            'buttonTextWeight',
            'buttonFont'
        ];
    
        keysToSync.forEach(key => {
            if (mobileSettings[key]) {
                mergedProps[key] = mobileSettings[key];
            }
        });
    }

    setMobileHorizontalSliderStyles(mobileSettings, mergedProps){

        if(mobileSettings.tickColor){
            mergedProps.tickColor = util._fixColorFormat(mobileSettings.tickColor);
        }

        if(mobileSettings.tickInterval){
            mergedProps.tickInterval = mobileSettings.tickInterval;
        }

        if(mobileSettings.sliderColor){
            mergedProps.sliderColor = util._fixColorFormat(mobileSettings.sliderColor);
        }
    }

    setMobileHorizontalSeparatorStyles(mobileSettings, mergedProps){

        if(mobileSettings.lineColor){
            mergedProps.lineColor = util._fixColorFormat(mobileSettings.lineColor);
        }
    }

    setMobileAlignmentSetting(mobileSettings, mergedProps){
        if (mobileSettings.textAlign) {
            mergedProps.align = mobileSettings.textAlign;
        }
        // As a fallback, check for the old 'align' property for backward compatibility.
        else if (mobileSettings.align) {
            mergedProps.align = mobileSettings.align;
        }
    }

    setMobileTextStyles(mobileSettings, mergedProps){
        if (mobileSettings.color !== undefined && mobileSettings.color !== null) {
            mergedProps.styleColorText = util._fixColorFormat(mobileSettings.color);
        }
        else if (mobileSettings.textColor !== undefined && mobileSettings.textColor !== null) {
            mergedProps.styleColorText = util._fixColorFormat(mobileSettings.textColor);
        }

        mergedProps.font = mobileSettings.font;

        if (mergedProps.font) {
            const fonts = mergedProps.font.split(',');
            if (fonts[0]) {
                mergedProps.fontFamily = fonts[0].trim();
            }
            if (fonts[1]) {
                mergedProps.fontSize = fonts[1].trim();
            }
            if (fonts[2]) {
                mergedProps.textWeight = fonts[2].trim() + ' !important';
            }
            mergedProps.textStyle = fonts[3] ? fonts[3].trim() + ' !important' : 'normal !important';
        }
        else {
            if (mobileSettings.fontFamily) {
                mergedProps.fontFamily = mobileSettings.fontFamily;
            }
            if (mobileSettings.fontSize) {
                mergedProps.fontSize = mobileSettings.fontSize;
            }
            if (mobileSettings.fontWeight) {
                mergedProps.textWeight = mobileSettings.fontWeight + ' !important';
            }
            if (mobileSettings.fontStyle) {
                mergedProps.textStyle = mobileSettings.fontStyle + ' !important';
            }
        }
    }

    setMobileBorderStyles(mobileSettings, mergedProps){
        if(mobileSettings.borderColor){
            mergedProps.borderColor = util._fixColorFormat(mobileSettings.borderColor);
        }

        mergedProps.border = mobileSettings.border;
        if (mergedProps.border) {
            const border = mergedProps.border.split(',');
            if (border[0]) {
                mergedProps.borderSize = border[0];
            }
            if (border[1]) {
                mergedProps.borderStyle = border[1];
            }
            if (border[2]) {
                mergedProps.cornerRadius = border[2];
            }
        }

        if(mobileSettings.cornerRadius){
            mergedProps.cornerRadius = mobileSettings.cornerRadius;
        }
    }

    reorderSections(pageId, sections) {
        let pageSections = listService.getSectionsByPageId(pageId);
        pageSections && pageSections.forEach(pageSection => {
            const section = sections.find(s => s.id === pageSection.id);
            if (section) {
                pageSection.index = section.index;
            }
        });
    }

    reorderTwoColumnsSections(sections, pageId, order) {
        let mobileColumn = listService.getColumn(pageId, order);
        if (mobileColumn) {
            if (!mobileColumn.sections) {
                mobileColumn.sections = Immutable.Map();
            }
            else {
                mobileColumn.sections = mobileColumn.sections.clear();
            }
        }
        else {
            const columnName = order === 1 ? 'Column 1' : 'Column 2';
            listService.createColumn(columnName, order, pageId);
            mobileColumn = listService.getColumn(pageId, order);
        }

        sections.forEach(section => {
            mobileColumn.sections = mobileColumn.sections.set(section.id, {id: section.id, order: section.index});
        });
    }
}
const mobileListService = new MobileListService();
export default mobileListService;
